/**
 * AccountPage – account management with two layouts:
 *   - Regular users: simple 3-tab horizontal strip (Preferences, Profile, Security)
 *   - Admins and read-only viewers (can_view_admin): sidebar with Personal + Administration;
 *     viewers see the same admin screens with controls disabled (isReadOnly) and API write protection.
 * Replaces the basic ProfilePage when the user clicks their initials in TopNav.
 */
import { lazy, Suspense, useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { getAuthHeaders, getAccessToken, getRefreshToken } from '../utils/auth.js'
import AdminSubTabs from './AdminSubTabs.jsx'
import PreferencesPage from './PreferencesPage.jsx'
import StateBanner from './StateBanner/StateBanner.jsx'
import { readSettingsDeepLinkHash } from './admin/adminConfigNavigation.js'

const ItemManagementAdmin = lazy(() => import('../ItemManagementAdmin.jsx'))
const AgentAnalyticsAdmin = lazy(() => import('../AgentAnalyticsAdmin.jsx'))
const AdminConfig = lazy(() => import('../AdminConfig.jsx'))
const AdminPrompts = lazy(() => import('../AdminPrompts.jsx'))

function AdminPanelLoading() {
  return (
    <p className="account-admin-chunk-loading" aria-live="polite">
      <i className="fas fa-spinner fa-spin icon-sm" aria-hidden="true" /> Loading…
    </p>
  )
}

function AdminLazyPanel({ children }) {
  return <Suspense fallback={<AdminPanelLoading />}>{children}</Suspense>
}

const DEMO_ACCOUNT_EMAIL = 'demo@martinisbythelake.com'

/** Visible heading for admin sidebar sections that otherwise render only a card (Profile, Security). */
function AccountAdminSectionHeader({ title, subtitle, iconClass }) {
  return (
    <header className="account-admin-section-header">
      <h2 className="account-admin-section-title">
        {iconClass ? (
          <i className={`fas ${iconClass} icon-base icon-primary icon-spacing-base`} aria-hidden="true" />
        ) : null}
        {title}
      </h2>
      {subtitle ? (
        <p className="page-subtitle" style={{ margin: 0 }}>{subtitle}</p>
      ) : null}
    </header>
  )
}

function normalize(s) {
  return (s ?? '').trim()
}
function normalizeEmail(s) {
  return normalize(s).toLowerCase()
}

function profileInitials(u) {
  if (!u) return '?'
  const f = (u.first_name || '').trim()
  const l = (u.last_name || '').trim()
  if (f && l) return (f[0] + l[0]).toUpperCase()
  if (f.length >= 2) return f.slice(0, 2).toUpperCase()
  if (f) return f.charAt(0).toUpperCase()
  const e = (u.email || '').trim()
  if (e.length >= 2) return e.slice(0, 2).toUpperCase()
  if (e) return e.charAt(0).toUpperCase()
  return '?'
}

function profileDisplayName(u) {
  if (!u) return ''
  const f = (u.first_name || '').trim()
  const l = (u.last_name || '').trim()
  const combined = `${f} ${l}`.trim()
  if (combined) return combined
  return (u.email || '').trim() || 'Account'
}

function shouldShowEmailUnverifiedBanner(user) {
  if (!user || user.email_verified !== false || !user.created_at) return false
  const createdAt = new Date(user.created_at)
  if (Number.isNaN(createdAt.getTime())) return false
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
  return Date.now() - createdAt.getTime() >= sevenDaysMs
}

function EmailUnverifiedBanner() {
  return (
    <StateBanner
      variant="urgent"
      eyebrow="Verify email"
      headline="Your kitchen won't sync until you verify."
      body={<>Verify <b>your email</b> so pantry and plans stay connected.</>}
      className="account-state-banner"
    />
  )
}

function ProfileSection({ user, isDemoAccount, checkAuth }) {
  const { isAdmin, canViewAdmin } = useAuth()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [savedFirstName, setSavedFirstName] = useState('')
  const [savedLastName, setSavedLastName] = useState('')
  const [savedEmail, setSavedEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const isDirty =
    normalize(firstName) !== normalize(savedFirstName) ||
    normalize(lastName) !== normalize(savedLastName)

  useEffect(() => {
    if (user) {
      const f = user.first_name ?? ''
      const l = user.last_name ?? ''
      const e = user.email ?? ''
      setFirstName(f)
      setLastName(l)
      setSavedFirstName(f)
      setSavedLastName(l)
      setSavedEmail(e)
    }
  }, [user])

  function handleCancel() {
    setError(null)
    setMessage(null)
    setFirstName(savedFirstName)
    setLastName(savedLastName)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setMessage(null)
    const trimmedEmail = normalizeEmail(savedEmail)
    if (!trimmedEmail) {
      setError('Email is missing from your account.')
      return
    }
    setSaving(true)
    try {
      const res = await fetch('/auth/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          first_name: (firstName || '').trim() || null,
          last_name: (lastName || '').trim() || null,
          email: trimmedEmail,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const raw = data.detail
        const detail =
          typeof raw === 'object' && raw?.message
            ? raw.message
            : Array.isArray(raw)
            ? raw.map((d) => d.msg || d).join(' ')
            : raw || `Request failed (${res.status})`
        setError(detail)
        return
      }
      setMessage('Profile updated.')
      const newFirst = (firstName || '').trim() || null
      const newLast = (lastName || '').trim() || null
      setSavedFirstName(newFirst ?? '')
      setSavedLastName(newLast ?? '')
      await checkAuth()
    } catch (err) {
      setError(err.message || 'Failed to update profile.')
    } finally {
      setSaving(false)
    }
  }

  const displayName = profileDisplayName({
    ...user,
    first_name: firstName,
    last_name: lastName,
  })
  const emailShown = savedEmail || user?.email || ''
  const initials = profileInitials({
    ...user,
    first_name: firstName,
    last_name: lastName,
  })

  return (
    <div className="account-profile-screen">
      <header className="account-profile-panel-header">
        <h2 className="account-profile-panel-title">Profile</h2>
        <p className="account-profile-panel-subtitle">
          Your identity and display name
        </p>
      </header>

      {isDemoAccount && (
        <div className="page-alert page-alert-warning" role="alert">
          This is the demo account. Profile changes are not allowed.
        </div>
      )}

      <div className="account-profile-avatar-header">
        <div className="account-profile-avatar-wrap">
          <div className="account-profile-avatar-circle" aria-hidden="true">
            {initials}
          </div>
          <button
            type="button"
            className="account-profile-avatar-edit"
            aria-label="Profile photo — coming soon"
            title="Coming soon"
          >
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
        </div>
        <div className="account-profile-avatar-info">
          <div className="account-profile-avatar-name">{displayName}</div>
          <div className="account-profile-avatar-email">{emailShown}</div>
          {isAdmin ? (
            <div className="account-profile-role-badge">
              <i className="fas fa-shield-halved" aria-hidden="true" />
              Admin
            </div>
          ) : canViewAdmin ? (
            <div className="account-profile-role-badge">
              <i className="fas fa-eye" aria-hidden="true" />
              Viewer
            </div>
          ) : null}
        </div>
      </div>

      <form className="account-profile-form-card" onSubmit={handleSubmit}>
        <div className="account-profile-form-card-header">
          <div className="account-profile-form-card-title">
            <i className="far fa-id-card" aria-hidden="true" />
            Personal Information
          </div>
        </div>
        <div className="account-profile-form-card-body">
          {error ? (
            <div className="page-alert page-alert-error" role="alert">
              {error}
            </div>
          ) : null}
          {message ? (
            <div className="page-alert page-alert-success" role="status">
              {message}
            </div>
          ) : null}
          <div className="account-profile-form-row">
            <div className="account-profile-form-group">
              <label htmlFor="account-first-name" className="account-profile-label">
                First name
              </label>
              <input
                id="account-first-name"
                type="text"
                className="account-profile-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Optional"
                maxLength={100}
                disabled={isDemoAccount}
                autoComplete="given-name"
              />
            </div>
            <div className="account-profile-form-group">
              <label htmlFor="account-last-name" className="account-profile-label">
                Last name
              </label>
              <input
                id="account-last-name"
                type="text"
                className="account-profile-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Optional"
                maxLength={100}
                disabled={isDemoAccount}
                autoComplete="family-name"
              />
            </div>
          </div>
          <div className="account-profile-form-group">
            <label htmlFor="account-email" className="account-profile-label">
              Email address{' '}
              <span className="account-profile-label-hint">(cannot change)</span>
            </label>
            <input
              id="account-email"
              type="email"
              className="account-profile-input account-profile-input-disabled"
              value={emailShown}
              readOnly
              tabIndex={-1}
              aria-readonly="true"
              autoComplete="email"
            />
          </div>
        </div>
        <div className="account-profile-form-card-footer">
          <button
            type="button"
            className="secondary"
            disabled={isDemoAccount || !isDirty || saving}
            onClick={handleCancel}
            aria-label="Discard profile changes"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="primary"
            disabled={saving || isDemoAccount || !isDirty}
            aria-label={
              isDemoAccount
                ? 'Profile cannot be updated for demo account'
                : isDirty
                  ? 'Save profile changes'
                  : 'No changes to save'
            }
          >
            {saving ? (
              'Saving…'
            ) : (
              <>
                <i className="fas fa-check" aria-hidden="true" /> Save changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

function summarizeUserAgent(ua) {
  if (!ua || typeof ua !== 'string') return 'Unknown device'
  const l = ua.toLowerCase()
  if (l.includes('iphone')) return 'iPhone'
  if (l.includes('ipad')) return 'iPad'
  if (l.includes('android')) return 'Android'
  if (l.includes('mac os') || l.includes('macintosh')) return 'Mac'
  if (l.includes('windows')) return 'Windows PC'
  if (l.includes('linux')) return 'Linux'
  return 'Web browser'
}

function sessionActivityShort(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const mins = Math.floor((Date.now() - d.getTime()) / 60000)
  if (mins < 1) return 'Now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 48) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function passwordStrengthProfile(password) {
  if (!password) {
    return { pct: 0, label: '', hint: '', colorVar: 'var(--text-tertiary)' }
  }
  let score = 0
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++
  const pct = Math.min(100, Math.round((score / 5) * 100))
  if (score <= 1) {
    return {
      pct,
      label: 'Weak',
      hint: 'Use at least 8 characters; mix upper, lower, and numbers.',
      colorVar: 'var(--color-error)',
    }
  }
  if (score <= 3) {
    return {
      pct,
      label: 'Medium',
      hint: 'Add a symbol or a longer phrase to make it stronger.',
      colorVar: 'var(--color-warning, hsl(33, 100%, 50%))',
    }
  }
  return {
    pct,
    label: 'Strong',
    hint: 'Good password strength.',
    colorVar: 'var(--color-success, var(--color-dominant))',
  }
}

function sessionDeviceIconClass(ua) {
  if (!ua || typeof ua !== 'string') return 'fas fa-desktop'
  const l = ua.toLowerCase()
  if (l.includes('iphone') || l.includes('ipad') || l.includes('android')) {
    return 'fas fa-mobile-screen'
  }
  if (l.includes('mac') || l.includes('iphone')) {
    return 'fab fa-apple'
  }
  return 'fas fa-desktop'
}

function SecuritySection({ isDemoAccount }) {
  const { logout, logoutAllDevices } = useAuth()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [summaryError, setSummaryError] = useState('')
  const [revokingId, setRevokingId] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)

  const strength = passwordStrengthProfile(newPassword)

  async function loadSecuritySummary() {
    const access = getAccessToken()
    if (!access) {
      setSummary(null)
      setSummaryLoading(false)
      return
    }
    setSummaryError('')
    try {
      const refresh = getRefreshToken()
      const res = await fetch('/auth/security-summary', {
        headers: {
          Authorization: `Bearer ${access}`,
          ...(refresh ? { 'X-Refresh-Token': refresh } : {}),
        },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setSummaryError(typeof data.detail === 'string' ? data.detail : 'Could not load sessions.')
        setSummary(null)
        return
      }
      setSummary(data)
    } catch (err) {
      setSummaryError(err.message || 'Could not load sessions.')
      setSummary(null)
    } finally {
      setSummaryLoading(false)
    }
  }

  useEffect(() => {
    loadSecuritySummary()
  }, [])

  async function handleRevokeSession(sessionId, isCurrent) {
    if (isCurrent) {
      await logout()
      return
    }
    const access = getAccessToken()
    if (!access) return
    setRevokingId(sessionId)
    setSummaryError('')
    try {
      const res = await fetch(`/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setSummaryError(data.detail || 'Could not revoke session.')
        return
      }
      await loadSecuritySummary()
    } catch (err) {
      setSummaryError(err.message || 'Could not revoke session.')
    } finally {
      setRevokingId(null)
    }
  }

  async function handleLogoutAllDevices() {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Log out on all devices? You will need to sign in again everywhere.',
      )
    ) {
      return
    }
    try {
      await logoutAllDevices()
    } catch (err) {
      console.error(err)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setMessage(null)
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.')
      return
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.')
      return
    }
    setSaving(true)
    try {
      const res = await fetch('/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const raw = data.detail
        const detail =
          typeof raw === 'object' && raw?.message
            ? raw.message
            : Array.isArray(raw)
              ? raw.map((d) => d.msg || d).join(' ')
              : raw || `Request failed (${res.status})`
        setError(detail)
        return
      }
      setMessage(
        data.message ||
          'Password changed. Please sign in again on your phone and computer.',
      )
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSummaryLoading(true)
      await loadSecuritySummary()
    } catch (err) {
      setError(err.message || 'Failed to change password.')
    } finally {
      setSaving(false)
    }
  }

  const lastLogin = summary?.last_login
  const lastLoginPrimary = lastLogin
    ? new Date(lastLogin).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'
  const currentSession = summary?.sessions?.find((s) => s.is_current)
  const lastLoginSub =
    currentSession?.user_agent || currentSession?.ip_address
      ? [
          summarizeUserAgent(currentSession.user_agent),
          currentSession.ip_address || null,
        ]
          .filter(Boolean)
          .join(' · ')
      : typeof navigator !== 'undefined'
        ? summarizeUserAgent(navigator.userAgent)
        : ''

  return (
    <div className="account-security-screen">
      <header className="account-profile-panel-header">
        <h2 className="account-profile-panel-title">Security</h2>
        <p className="account-profile-panel-subtitle">
          Password management and active sessions
        </p>
      </header>

      <div className="account-security-layout">
        <form className="account-profile-form-card" onSubmit={handleSubmit}>
          <div className="account-profile-form-card-header">
            <div className="account-profile-form-card-title">
              <i className="fas fa-key" aria-hidden="true" />
              Change Password
            </div>
          </div>
          <div className="account-profile-form-card-body">
            {isDemoAccount && (
              <div className="page-alert page-alert-warning" role="alert">
                This is the demo account. Password changes are not allowed.
              </div>
            )}
            <div className="account-profile-form-group">
              <label htmlFor="account-old-password" className="account-profile-label">
                Current password
              </label>
              <input
                id="account-old-password"
                type="password"
                className="account-profile-input"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                disabled={isDemoAccount}
                autoComplete="current-password"
              />
            </div>
            <div className="account-profile-form-group">
              <label htmlFor="account-new-password" className="account-profile-label">
                New password <span className="account-profile-label-hint">(min 8 characters)</span>
              </label>
              <input
                id="account-new-password"
                type="password"
                className="account-profile-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                disabled={isDemoAccount}
                autoComplete="new-password"
              />
              {newPassword ? (
                <div className="account-pw-strength">
                  <div className="account-pw-strength-bar">
                    <div
                      className="account-pw-strength-fill"
                      style={{ width: `${strength.pct}%` }}
                    />
                  </div>
                  <div className="account-pw-strength-label">
                    Strength:{' '}
                    <strong style={{ color: strength.colorVar }}>{strength.label}</strong>
                    {strength.hint ? ` — ${strength.hint}` : null}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="account-profile-form-group">
              <label htmlFor="account-confirm-password" className="account-profile-label">
                Confirm new password
              </label>
              <input
                id="account-confirm-password"
                type="password"
                className="account-profile-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                disabled={isDemoAccount}
                autoComplete="new-password"
              />
            </div>
            {error ? (
              <div className="page-alert page-alert-error" role="alert">
                {error}
              </div>
            ) : null}
            {message ? (
              <div className="page-alert page-alert-success" role="status">
                {message}
              </div>
            ) : null}
          </div>
          <div className="account-profile-form-card-footer">
            <button
              type="submit"
              className="primary"
              disabled={saving || isDemoAccount || !oldPassword || !newPassword || !confirmPassword}
              aria-label={isDemoAccount ? 'Password cannot be changed for demo account' : 'Change password'}
            >
              {saving ? (
                'Saving…'
              ) : (
                <>
                  <i className="fas fa-check" aria-hidden="true" /> Change password
                </>
              )}
            </button>
          </div>
        </form>

        <div className="account-security-info-card">
          {summary?.password_changed_at ? (
            <div>
              <div className="account-security-info-label">Last password change</div>
              <div className="account-security-info-value">
                {new Date(summary.password_changed_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            </div>
          ) : null}

          <div>
            <div className="account-security-info-label">Last login</div>
            <div className="account-security-info-value">{lastLoginPrimary}</div>
            {lastLoginSub ? (
              <div className="account-security-info-sub">{lastLoginSub}</div>
            ) : null}
          </div>

          <div>
            <div className="account-security-info-label">Active sessions</div>
            <div className="account-security-info-value">
              {summaryLoading
                ? '…'
                : `${summary?.active_session_count ?? 0} device${(summary?.active_session_count ?? 0) === 1 ? '' : 's'}`}
            </div>
          </div>

          {summaryError ? (
            <div className="page-alert page-alert-error" role="alert">
              {summaryError}
            </div>
          ) : null}

          <div className="account-security-session-block">
            <div className="account-security-info-label" style={{ marginBottom: 'var(--space-2)' }}>
              Sessions
            </div>
            {summaryLoading ? (
              <p className="page-subtitle" style={{ margin: 0 }}>
                Loading sessions…
              </p>
            ) : !summary?.sessions?.length ? (
              <p className="page-subtitle" style={{ margin: 0 }}>
                No active sessions listed. They may expire quickly after login.
              </p>
            ) : (
              summary.sessions.map((s) => (
                <div key={s.id} className="account-security-session-item">
                  <i
                    className={`${sessionDeviceIconClass(s.user_agent)} account-security-session-icon`}
                    aria-hidden="true"
                  />
                  <div className="account-security-session-meta">
                    <div className="account-security-session-device">
                      {summarizeUserAgent(s.user_agent)}
                    </div>
                    <div className="account-security-session-when">
                      {[s.ip_address, sessionActivityShort(s.updated_at || s.created_at)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {s.is_current ? (
                    <span className="account-security-session-badge">This device</span>
                  ) : (
                    <button
                      type="button"
                      className="account-security-revoke-btn"
                      disabled={revokingId === s.id}
                      onClick={() => handleRevokeSession(s.id, false)}
                    >
                      {revokingId === s.id ? '…' : 'Revoke'}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            className="secondary"
            style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-sm)', alignSelf: 'flex-start' }}
            onClick={handleLogoutAllDevices}
          >
            <i className="fas fa-right-from-bracket" aria-hidden="true" /> Log out all devices
          </button>
        </div>
      </div>
    </div>
  )
}

// Admin sidebar items definition
const PERSONAL_ITEMS = [
  { id: 'preferences',  label: 'Preferences',  icon: 'fa-sliders-h' },
  { id: 'profile',      label: 'Profile',      icon: 'fa-user' },
  { id: 'security',     label: 'Security',     icon: 'fa-lock' },
]
const ADMIN_ITEMS = [
  { id: 'analytics',    label: 'Analytics',      icon: 'fa-chart-line' },
  { id: 'prompts',      label: 'Prompts',        icon: 'fa-scroll' },
  { id: 'config',       label: 'Settings',       icon: 'fa-cog' },
  { id: 'item-admin',   label: 'Items',          icon: 'fa-tags' },
  { id: 'documents',    label: 'Documents',      shortLabel: 'Docs', icon: 'fa-folder' },
]
const ADMIN_IDS = new Set(ADMIN_ITEMS.map((i) => i.id))

export default function AccountPage({ subTab: initialSubTab = 'preferences', onSubTabChange, onPreferencesChanged, systemSubTab, setSystemSubTab, documentsContent }) {
  const { user, checkAuth, isAdmin, canViewAdmin, logout } = useAuth()
  const showAdministrationNav = isAdmin || canViewAdmin
  const [subTab, setSubTab] = useState(initialSubTab)

  useEffect(() => {
    setSubTab(initialSubTab)
  }, [initialSubTab])

  // Admin sidebar active destination — kept in sync with subTab + systemSubTab.
  // Derived once on mount: if the persisted tab was 'system-admin', restore the
  // admin sub-section from systemSubTab; otherwise use the top-level tab value.
  const [adminTab, setAdminTab] = useState(() => {
    if (typeof window !== 'undefined' && readSettingsDeepLinkHash()) return 'config'
    if (initialSubTab === 'system-admin') return systemSubTab || 'analytics'
    if (initialSubTab === 'item-admin') return 'item-admin'
    return initialSubTab || 'preferences'
  })

  useEffect(() => {
    if (initialSubTab === 'system-admin' && systemSubTab && ADMIN_IDS.has(systemSubTab)) {
      setAdminTab(systemSubTab)
    }
  }, [initialSubTab, systemSubTab])

  function handleSubTabChange(tab) {
    setSubTab(tab)
    onSubTabChange?.(tab)
  }

  function handleAdminTabChange(id) {
    setAdminTab(id)
    if (ADMIN_IDS.has(id)) {
      // Map admin sections back to 'system-admin' so App.jsx localStorage key
      // stays compatible, and sync the system sub-tab for deep-link persistence.
      handleSubTabChange('system-admin')
      setSystemSubTab?.(id)
    } else {
      handleSubTabChange(id)
    }
  }

  useEffect(() => {
    function navigateFromHash() {
      const hash = readSettingsDeepLinkHash()
      if (hash) {
        handleAdminTabChange('config')
      }
    }
    navigateFromHash()
    window.addEventListener('hashchange', navigateFromHash)
    return () => window.removeEventListener('hashchange', navigateFromHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleLogout() {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error (non-blocking):', error)
    }
  }

  const isDemoAccount = user?.email?.toLowerCase() === DEMO_ACCOUNT_EMAIL.toLowerCase()
  const showEmailUnverifiedBanner = shouldShowEmailUnverifiedBanner(user)

  if (!user) {
    return (
      <div className="profile-page admin-page">
        <p className="page-subtitle">Please log in to view and edit your account.</p>
      </div>
    )
  }

  // ── No admin/viewer flags: simple 3-tab strip (Preferences, Profile, Security)
  if (!showAdministrationNav) {
    const tabs = [
      {
        id: 'preferences',
        label: 'Preferences',
        icon: 'fa-sliders-h',
        content: <PreferencesPage onPreferencesChanged={onPreferencesChanged} />,
      },
      {
        id: 'profile',
        label: 'Profile',
        icon: 'fa-user',
        content: <ProfileSection user={user} isDemoAccount={isDemoAccount} checkAuth={checkAuth} />,
      },
      {
        id: 'security',
        label: 'Security',
        icon: 'fa-lock',
        content: <SecuritySection isDemoAccount={isDemoAccount} />,
      },
    ]
    return (
      <div className="profile-page admin-page">
        <header className="admin-page-header">
          <div>
            <h1 className="page-title">
              <i className="fas fa-user-circle icon-base icon-primary icon-spacing-base" aria-hidden="true" />
              Account
            </h1>
            <p className="page-subtitle" style={{ margin: 0 }}>
              Manage your profile and account security.
            </p>
          </div>
          <button type="button" className="secondary" onClick={handleLogout} aria-label="Log out of your account">
            <i className="fas fa-sign-out-alt icon-sm icon-muted" aria-hidden="true" /> Log out
          </button>
        </header>
        {showEmailUnverifiedBanner && <EmailUnverifiedBanner />}
        <AdminSubTabs
          tabs={tabs}
          activeTab={subTab}
          onTabChange={handleSubTabChange}
          tablistAriaLabel="Account sections"
        />
        <p id="account-demo-notice" className="sr-only">Demo account cannot be edited.</p>
      </div>
    )
  }

  // ── Admin: sidebar layout with Personal + Administration groups ───────────
  const contentMap = {
    profile: (
      <div className="account-admin-personal-section">
        <ProfileSection user={user} isDemoAccount={isDemoAccount} checkAuth={checkAuth} />
      </div>
    ),
    preferences: (
      <div className="account-admin-personal-section">
        <PreferencesPage onPreferencesChanged={onPreferencesChanged} />
      </div>
    ),
    security: (
      <div className="account-admin-personal-section">
        <SecuritySection isDemoAccount={isDemoAccount} />
      </div>
    ),
  }

  function renderAdministrationContent(tab) {
    switch (tab) {
      case 'item-admin':
        return (
          <AdminLazyPanel>
            <ItemManagementAdmin />
          </AdminLazyPanel>
        )
      case 'analytics':
        return (
          <AdminLazyPanel>
            <AgentAnalyticsAdmin />
          </AdminLazyPanel>
        )
      case 'prompts':
        return (
          <AdminLazyPanel>
            <AdminPrompts />
          </AdminLazyPanel>
        )
      case 'config':
        return (
          <AdminLazyPanel>
            <AdminConfig />
          </AdminLazyPanel>
        )
      case 'documents':
        return documentsContent
      default:
        return null
    }
  }

  return (
    <div className="account-admin-page">
      <header className="account-admin-page-header">
        <h1 className="account-admin-page-header-title">
          <i className="fas fa-user-circle icon-base icon-primary" aria-hidden="true" />
          Account
        </h1>
        <button
          type="button"
          className="secondary account-admin-page-header-logout"
          onClick={handleLogout}
          aria-label="Log out of your account"
        >
          <i className="fas fa-sign-out-alt icon-sm icon-muted" aria-hidden="true" /> Log out
        </button>
      </header>

      <div className="account-admin-layout">
        {/* Sidebar */}
        <aside className="account-admin-sidebar" aria-label="Account navigation">
          <div className="account-admin-sidebar-tabs-wrap">
            <nav>
              <div className="account-admin-sidebar-group">
                <div className="account-admin-sidebar-group-label">Personal</div>
                {PERSONAL_ITEMS.map(({ id, label, icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`account-admin-sidebar-item${adminTab === id ? ' active' : ''}`}
                    onClick={() => handleAdminTabChange(id)}
                    aria-current={adminTab === id ? 'page' : undefined}
                  >
                    <i className={`fas ${icon}`} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>

              <div className="account-admin-sidebar-group">
                <div className="account-admin-sidebar-group-label">Administration</div>
                {ADMIN_ITEMS.map(({ id, label, shortLabel, icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`account-admin-sidebar-item${adminTab === id ? ' active' : ''}`}
                    onClick={() => handleAdminTabChange(id)}
                    aria-current={adminTab === id ? 'page' : undefined}
                    aria-label={shortLabel ? label : undefined}
                  >
                    <i className={`fas ${icon}`} aria-hidden="true" />
                    {shortLabel ? (
                      <>
                        <span className="account-admin-sidebar-label-full">{label}</span>
                        <span className="account-admin-sidebar-label-short">{shortLabel}</span>
                      </>
                    ) : (
                      label
                    )}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="account-admin-content" aria-label={`${PERSONAL_ITEMS.concat(ADMIN_ITEMS).find((i) => i.id === adminTab)?.label ?? 'Account'} section`}>
          <p id="account-demo-notice" className="sr-only">Demo account cannot be edited.</p>
          {showEmailUnverifiedBanner && <EmailUnverifiedBanner />}
          {ADMIN_IDS.has(adminTab) ? renderAdministrationContent(adminTab) : (contentMap[adminTab] ?? null)}
        </main>
      </div>
    </div>
  )
}
